// ════════════════════════════════════════════════════════════
// /depenses/new — saisie manuelle (ECR light, mobile-first)
// ════════════════════════════════════════════════════════════
// Formulaire de création d'une dépense :
//   - fournisseur (autocomplete + auto-création + pré-fill defaults)
//   - montant, mode de paiement (wallet / chèque / transfert SWIFT)
//   - compte de trésorerie débité (wallet → Bankily, chèque/SWIFT → banque)
//   - devise étrangère EUR/USD : source banque (SWIFT) vs marché noir
//   - upload manuel d'un justificatif (photo/PDF → Supabase Storage)
//   - Submit : INSERT depense, puis navigate vers détail
// ════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm, type SubmitHandler } from 'react-hook-form';
import {
  ArrowLeft,
  Save,
  Loader2,
  Camera,
  Upload,
  CheckCircle2,
  X,
  Globe,
  FileText,
  AlertTriangle,
  PenLine,
  RefreshCw,
  ScanLine,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useCreateDepense,
  useFournisseurSearch,
  useUpsertFournisseur,
  useUpdateFournisseurDefaults,
  useOcrExpenseFacture,
} from '@/queries/depenses';
import { useComptesTresorerie } from '@/queries/tresorerie';
import { uploadJustificatif, fileToBase64 } from '@/lib/uploadStorage';
import { isOcrAvailable, type OcrFactureResult } from '@/lib/aiGateway';
import { supabase } from '@/lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { formatMRU, formatAmount } from '@ecr/core/utils';
import {
  canCreateDepense,
  isDoubleValidationRequired,
  SEUIL_DOUBLE_VALIDATION_MRU,
} from '@ecr/core/depenses';
import type { CompteTresorerie } from '@ecr/core/paiements';

export const Route = createFileRoute('/_authed/depenses/new')({
  staticData: { crumb: 'Nouvelle dépense' },
  component: NewDepensePage,
});

// ─── Form shape (simplifié, validation runtime ci-dessous) ─
interface DepenseFormShape {
  beneficiaire: string;
  fournisseurNom: string;
  fournisseurId: string | null;
  nifFournisseur: string;
  numFacture: string;
  dateFacture: string; // YYYY-MM-DD
  montant: number;
  modePaiementSouhaite: 'wallet' | 'cheque' | 'transfer';
  devise: 'MRU' | 'EUR' | 'USD' | '';
  /** Source devise (marché noir ou banque SWIFT) — choisi via modal si facture EUR/USD. */
  deviseSource: 'banque' | 'marche_noir' | '';
  marcheNoirContact: string;
  /** FK comptes_tresorerie : compte débité (chèque / SWIFT ; Bankily auto en wallet). */
  paidCompteId: string;
  walletType: '' | 'bankily' | 'masrivi' | 'sedad' | 'bimbank' | 'click' | 'bcipay';
  walletNumero: string;
  categorie: string;
  description: string;
  /** Devise de la facture d'origine (EUR/USD) + montant origine + taux MRU. */
  ocrDeviseDetectee: 'MRU' | 'EUR' | 'USD' | null;
  ocrMontantOrigine: number | null;
  ocrTauxChangeMru: number | null;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function NewDepensePage() {
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const userEmail = session?.user?.email ?? '';
  const userId = session?.user?.id ?? null;

  const { data: comptes = [] } = useComptesTresorerie();
  // Comptes bancaires ECR (chèque / SWIFT) — jamais la Caisse Mauripe.
  const comptesBanque = comptes.filter((c) => c.type === 'banque');
  const compteBankily = comptes.find((c) => c.code === 'BANKILY');
  const upsertFournisseur = useUpsertFournisseur();
  const updateDefaults = useUpdateFournisseurDefaults();
  const createDepense = useCreateDepense();

  const [knownFournisseurAlert, setKnownFournisseurAlert] = useState<string | null>(null);
  /** Modal devise (marché noir vs SWIFT). Ouvert quand la facture est en EUR/USD. */
  const [showDeviseModal, setShowDeviseModal] = useState(false);
  /** Justificatif uploadé en Storage après création de la dépense. */
  const [justifFile, setJustifFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // ─── OCR facture (Worker ecr-ai-gateway) ─────────────────
  // phase 'upload' : photo/PDF → analyse IA → form pré-rempli.
  // « Saisie manuelle » saute directement au form.
  const ocrEnabled = isOcrAvailable();
  const [phase, setPhase] = useState<'upload' | 'processing' | 'form'>(
    ocrEnabled ? 'upload' : 'form',
  );
  const [ocrResult, setOcrResult] = useState<OcrFactureResult | null>(null);
  const ocrMutation = useOcrExpenseFacture();

  const form = useForm<DepenseFormShape>({
    defaultValues: {
      beneficiaire: '',
      fournisseurNom: '',
      fournisseurId: null,
      nifFournisseur: '',
      numFacture: '',
      // Date facture = today par défaut (feedback Sidi : auto-fill)
      dateFacture: todayYmd(),
      montant: 0,
      modePaiementSouhaite: 'wallet',
      devise: '',
      deviseSource: '',
      marcheNoirContact: '',
      paidCompteId: '',
      walletType: '',
      walletNumero: '',
      categorie: '',
      description: '',
      ocrDeviseDetectee: null,
      ocrMontantOrigine: null,
      ocrTauxChangeMru: null,
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = form;

  const modePaiement = watch('modePaiementSouhaite');
  const devise = watch('devise');
  const fournisseurNomValue = watch('fournisseurNom');

  // Auto-suggest fournisseur existant pendant que l'user tape
  const { data: fournisseurMatches = [] } = useFournisseurSearch(fournisseurNomValue);

  // Auto-detect fournisseur connu (si tape un nom proche) + applique defaults
  useEffect(() => {
    if (!fournisseurNomValue || fournisseurNomValue.length < 3) return;
    const exact = fournisseurMatches.find(
      (f) => f.nom.toLowerCase() === fournisseurNomValue.toLowerCase(),
    );
    if (exact && exact.id !== watch('fournisseurId')) {
      setValue('fournisseurId', exact.id);
      if (exact.defaultCategorie && !watch('categorie')) {
        setValue('categorie', exact.defaultCategorie);
      }
      if (exact.defaultModePaiement) {
        setValue('modePaiementSouhaite', exact.defaultModePaiement);
      }
      if (exact.defaultWalletType) {
        setValue('walletType', exact.defaultWalletType);
      }
      if (exact.defaultWalletNumero) {
        setValue('walletNumero', exact.defaultWalletNumero);
      }
      if (exact.defaultPaidCompte) {
        setValue('paidCompteId', exact.defaultPaidCompte);
      }
      if (exact.defaultDevise) {
        setValue('devise', exact.defaultDevise);
      }
      setKnownFournisseurAlert(
        `Fournisseur connu : ${exact.nbFactures} facture(s) précédente(s). Préférences pré-remplies.`,
      );
    }
  }, [fournisseurNomValue, fournisseurMatches, setValue, watch]);

  // ═════════════════════════════════════════════════════════
  // Justificatif manuel (photo/PDF → Storage après création)
  // ═════════════════════════════════════════════════════════
  function handleJustifSelected(file: File) {
    const isPdf = file.type === 'application/pdf';
    const sizeLimit = isPdf ? 10_000_000 : 5_000_000;
    if (file.size > sizeLimit) {
      toast.error(
        `Fichier trop lourd (max ${sizeLimit / 1_000_000} MB pour ${isPdf ? 'PDF' : 'image'}).`,
      );
      return;
    }
    setJustifFile(file);
    if (!isPdf) {
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(typeof reader.result === 'string' ? reader.result : null);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  }

  // ═════════════════════════════════════════════════════════
  // OCR : photo/PDF → Worker → pré-remplissage du form
  // ═════════════════════════════════════════════════════════
  async function handleOcrFileSelected(file: File) {
    const isPdf = file.type === 'application/pdf';
    const sizeLimit = isPdf ? 10_000_000 : 5_000_000;
    if (file.size > sizeLimit) {
      toast.error(
        `Fichier trop lourd (max ${sizeLimit / 1_000_000} MB pour ${isPdf ? 'PDF' : 'image'}).`,
      );
      return;
    }
    // Le fichier scanné devient aussi le justificatif de la demande.
    handleJustifSelected(file);
    setPhase('processing');
    try {
      const imageBase64 = await fileToBase64(file);
      const res = await ocrMutation.mutateAsync({
        imageBase64,
        mimeType: file.type || 'image/jpeg',
      });
      setOcrResult(res);
      applyOcrToForm(res);
      setPhase('form');
      if (res.devise_facture !== 'MRU') {
        setShowDeviseModal(true);
      }
      toast.success(`Facture analysée (confiance ${res.ocr_confiance}) — vérifie et complète.`);
    } catch (e) {
      toast.error(
        `Analyse IA échouée : ${e instanceof Error ? e.message : 'inconnu'} — saisis à la main, la photo reste attachée.`,
      );
      setPhase('form');
    }
  }

  /** Mappe le résultat OCR sur le formulaire (l'utilisateur vérifie ensuite). */
  function applyOcrToForm(res: OcrFactureResult) {
    if (res.fournisseur_nom) setValue('fournisseurNom', res.fournisseur_nom);
    if (res.beneficiaire_suggested) setValue('beneficiaire', res.beneficiaire_suggested);
    if (res.nif_fournisseur) setValue('nifFournisseur', res.nif_fournisseur);
    if (res.num_facture) setValue('numFacture', res.num_facture);
    if (res.date_facture) setValue('dateFacture', res.date_facture);
    if (res.categorie_suggested) setValue('categorie', res.categorie_suggested);
    if (res.notes) setValue('description', res.notes);
    if (res.devise_facture === 'MRU') {
      if (res.montant_total > 0) setValue('montant', res.montant_total);
    } else {
      // Facture EUR/USD : pré-remplit les champs devise, la modal SWIFT vs
      // marché noir s'ouvre juste après pour trancher le mode + le taux.
      setValue('ocrDeviseDetectee', res.devise_facture);
      setValue('ocrMontantOrigine', res.montant_origine ?? res.montant_total);
      setValue('ocrTauxChangeMru', res.taux_change_mru);
      if (res.montant_total > 0) setValue('montant', Math.round(res.montant_total));
    }
  }

  /** Retire la devise étrangère : la dépense redevient une dépense MRU domestique. */
  function clearDeviseEtrangere() {
    setValue('ocrDeviseDetectee', null);
    setValue('ocrMontantOrigine', null);
    setValue('ocrTauxChangeMru', null);
    setValue('deviseSource', '');
    setValue('marcheNoirContact', '');
    setValue('devise', '');
  }

  // ═════════════════════════════════════════════════════════
  // Submit
  // ═════════════════════════════════════════════════════════
  const onSubmit: SubmitHandler<DepenseFormShape> = async (values) => {
    // Validation montant
    if (values.montant <= 0) {
      toast.error('Montant doit être strictement positif');
      return;
    }
    // Sprint 3.5 : beneficiaire = fournisseur (Sidi cache le champ). Donc
    // si fournisseur vide, l'insert plantera. Valide en amont avec un toast clair.
    const beneficiaireEffectif = values.beneficiaire?.trim() || values.fournisseurNom?.trim() || '';
    if (beneficiaireEffectif.length === 0) {
      toast.error('Fournisseur obligatoire (sert aussi de bénéficiaire du paiement)');
      return;
    }

    // Validation mode paiement (le schema Zod existe mais n'est pas branche au submit) :
    // ECR n'a qu'un seul wallet (Bankily) → type imposé, seul le numéro se saisit.
    if (values.modePaiementSouhaite === 'wallet' && !values.walletNumero?.trim()) {
      toast.error('Numéro Bankily du bénéficiaire obligatoire');
      return;
    }
    const walletType = values.modePaiementSouhaite === 'wallet' ? ('bankily' as const) : null;
    // Compte de trésorerie débité : wallet → Bankily auto, sinon choix banque.
    const paidCompteId =
      values.modePaiementSouhaite === 'wallet' ? (compteBankily?.id ?? '') : values.paidCompteId;
    if (values.modePaiementSouhaite === 'cheque' && !paidCompteId) {
      toast.error('Compte bancaire émetteur du chèque obligatoire');
      return;
    }
    if (values.modePaiementSouhaite === 'transfer') {
      if (values.devise !== 'EUR' && values.devise !== 'USD') {
        toast.error('Mode SWIFT/transfert : devise EUR ou USD obligatoire');
        return;
      }
      if (!paidCompteId) {
        toast.error('Compte bancaire émetteur obligatoire pour un transfert SWIFT');
        return;
      }
    }

    // Validation marché noir : contact obligatoire
    if (values.deviseSource === 'marche_noir' && !values.marcheNoirContact.trim()) {
      toast.error('Nom du contact marché noir obligatoire');
      return;
    }
    if (
      values.deviseSource === 'marche_noir' &&
      (!values.ocrTauxChangeMru || values.ocrTauxChangeMru <= 0)
    ) {
      toast.error('Taux de change requis pour le marché noir');
      return;
    }

    try {
      // 1. Resolve/create fournisseur si nom rempli
      let fournisseurId = values.fournisseurId;
      if (!fournisseurId && values.fournisseurNom.trim().length > 0) {
        const fournisseur = await upsertFournisseur.mutateAsync({
          nom: values.fournisseurNom.trim(),
          nif: values.nifFournisseur.trim() || null,
          userId,
        });
        fournisseurId = fournisseur.id;
      }

      // 2. INSERT depense
      const depense = await createDepense.mutateAsync({
        beneficiaire: beneficiaireEffectif,
        fournisseurId,
        fournisseurNom: values.fournisseurNom || null,
        categorie: values.categorie || null,
        description: values.description || null,
        montant: values.montant,
        modePaiementSouhaite: values.modePaiementSouhaite,
        devise:
          values.modePaiementSouhaite === 'transfer'
            ? values.devise === 'EUR' || values.devise === 'USD'
              ? values.devise
              : null
            : null,
        deviseSource: values.deviseSource || null,
        marcheNoirContact:
          values.deviseSource === 'marche_noir' ? values.marcheNoirContact || null : null,
        paidCompteId: paidCompteId || null,
        walletType,
        walletNumero: walletType ? values.walletNumero || null : null,
        numFacture: values.numFacture || null,
        dateFacture: values.dateFacture ? new Date(values.dateFacture) : null,
        nifFournisseur: values.nifFournisseur || null,
        ocrDeviseDetectee: values.ocrDeviseDetectee,
        ocrMontantOrigine: values.ocrMontantOrigine,
        ocrTauxChangeMru: values.ocrTauxChangeMru,
        // Traçabilité OCR + lignes de facture extraites (renumérotées par facture)
        ocrUsed: !!ocrResult,
        ocrConfiance: ocrResult?.ocr_confiance ?? null,
        ocrRawJson: ocrResult ?? null,
        ocrFacturesDetail: ocrResult?.factures ?? null,
        ocrModel: ocrResult?.ocr_model ?? null,
        lignes: ocrResult
          ? ocrResult.factures.flatMap((f) =>
              f.lignes.map((l, idx) => ({
                ligneNo: idx + 1,
                libelle: l.libelle,
                quantite: l.quantite,
                unite: l.unite,
                prixUnitaire: l.prix_unitaire,
                montantLigne: l.montant_ligne,
                factureIndex: f.facture_index,
                ocrConfiance: l.ocr_confiance,
              })),
            )
          : undefined,
        userId,
        userEmail,
      });

      // 3. Mémorise les defaults sur le fournisseur (Q3 : 2e+ facture pré-fill)
      if (fournisseurId) {
        await updateDefaults.mutateAsync({
          fournisseurId,
          defaultCategorie: values.categorie || null,
          defaultModePaiement: values.modePaiementSouhaite,
          defaultWalletType: walletType,
          defaultWalletNumero: walletType ? values.walletNumero || null : null,
          defaultPaidCompte: paidCompteId || null,
          defaultDevise:
            values.modePaiementSouhaite === 'transfer'
              ? values.devise === 'EUR' || values.devise === 'USD'
                ? values.devise
                : null
              : null,
        });
      }

      // Upload le justificatif dans Storage et patche la dépense (kind=demande)
      if (justifFile) {
        try {
          const { path } = await uploadJustificatif({
            tenantId: depense.tenantId,
            depenseId: depense.id,
            kind: 'demande',
            file: justifFile,
          });
          const sbAny = supabase as unknown as SupabaseClient;
          await sbAny
            .from('depenses')
            .update({ justificatifs_demande_urls: [path] })
            .eq('id', depense.id);
        } catch (e) {
          // Non bloquant : la dépense est créée, juste pas de facture archivée
          toast.warning(
            `Dépense créée mais facture non archivée : ${e instanceof Error ? e.message : 'inconnu'}`,
          );
        }
      }

      toast.success(
        depense.approvalStatus === 'pending'
          ? `Dépense ${depense.code} créée — en attente de validation par l'autre DAF`
          : `Dépense ${depense.code} créée`,
      );
      void navigate({ to: '/depenses/$id', params: { id: depense.id } });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur création';
      toast.error(msg);
    }
  };

  // ═════════════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════════════
  // Demander un paiement = DAF only (Sidi/Abidine).
  const accessDenied = profile && !canCreateDepense(profile.role);
  if (!accessDenied && phase === 'upload') {
    return (
      <UploadPhase
        onFileSelected={(f) => void handleOcrFileSelected(f)}
        onManual={() => setPhase('form')}
      />
    );
  }
  if (!accessDenied && phase === 'processing') {
    return <ProcessingPhase />;
  }

  if (profile && !canCreateDepense(profile.role)) {
    return (
      <div className="mx-auto max-w-md space-y-4 py-12">
        <Card>
          <CardContent className="space-y-3 py-6 text-center">
            <p className="text-foreground font-medium">Réservé au DAF</p>
            <p className="text-muted-foreground text-sm">
              Seuls Sidi et Abidine peuvent demander un paiement. Fatimetou exécute ensuite les
              paiements demandés depuis la liste des dépenses.
            </p>
            <Link to="/depenses">
              <Button variant="outline" className="mt-2">
                Retour aux dépenses
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/depenses">
          <Button variant="ghost" size="icon" aria-label="Retour">
            <ArrowLeft className="size-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
            Nouvelle dépense
          </h1>
          <p className="text-muted-foreground text-sm">Saisis et vérifie les informations</p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
        className="space-y-5 pb-[calc(9rem+env(safe-area-inset-bottom,0px))] sm:pb-0"
      >
        {/* Bandeau résultat OCR */}
        {ocrResult && (
          <OcrResultBanner
            result={ocrResult}
            imagePreview={imagePreview}
            onRescan={() => setPhase('upload')}
          />
        )}

        {/* Anomalies pré-détectées par l'OCR */}
        {ocrResult && ocrResult.anomalies_pre_detected.length > 0 && (
          <Card className="border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/30">
            <CardContent className="space-y-1.5 py-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-4 shrink-0 text-orange-600 dark:text-orange-400" />
                <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                  Points relevés par l&apos;analyse
                </p>
              </div>
              {ocrResult.anomalies_pre_detected.map((a, i) => (
                <p key={i} className="pl-6 text-xs text-orange-800 dark:text-orange-200">
                  {a.message}
                </p>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Fournisseur known alert */}
        {knownFournisseurAlert && (
          <Card className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
            <CardContent className="flex items-start gap-3 py-3">
              <CheckCircle2 className="size-5 shrink-0 text-blue-600 dark:text-blue-400" />
              <p className="text-sm text-blue-900 dark:text-blue-100">{knownFournisseurAlert}</p>
              <button
                type="button"
                onClick={() => setKnownFournisseurAlert(null)}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                aria-label="Fermer"
              >
                <X className="size-4" />
              </button>
            </CardContent>
          </Card>
        )}

        {/* Devise étrangère badge */}
        {watch('ocrDeviseDetectee') && watch('ocrDeviseDetectee') !== 'MRU' && (
          <Card className="border-purple-200 bg-purple-50 dark:border-purple-900 dark:bg-purple-950/30">
            <CardContent className="flex items-start gap-3 py-3">
              <Globe className="size-5 shrink-0 text-purple-600 dark:text-purple-400" />
              <div className="flex-1 text-sm">
                <p className="font-medium text-purple-900 dark:text-purple-100">
                  Facture en {watch('ocrDeviseDetectee')}
                  {watch('deviseSource') === 'banque' && ' · SWIFT bancaire 🏦'}
                  {watch('deviseSource') === 'marche_noir' && ' · Marché noir 🤝'}
                </p>
                <p className="text-purple-700 dark:text-purple-300">
                  {watch('deviseSource') === 'marche_noir' &&
                    watch('marcheNoirContact') &&
                    `Contact: ${watch('marcheNoirContact')} · taux ${watch('ocrTauxChangeMru')} · `}
                  {watch('deviseSource') === 'banque' &&
                    `Compte: ${comptesBanque.find((c) => c.id === watch('paidCompteId'))?.label ?? '—'} · taux à l'upload SWIFT · `}
                  {!watch('deviseSource')
                    ? 'Choisis la source : SWIFT bancaire ou marché noir'
                    : watch('deviseSource') === 'banque'
                      ? `Total : ${formatAmount(watch('montant'), (watch('devise') as 'EUR' | 'USD') || 'EUR')}`
                      : `Total : ${formatMRU(watch('montant'))}`}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeviseModal(true)}
                >
                  {watch('deviseSource') ? 'Changer' : 'Choisir'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={clearDeviseEtrangere}>
                  Retirer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fournisseur fuzzy autocomplete */}
        <Card>
          <CardContent className="space-y-4 pt-6">
            <Field label="Fournisseur" required>
              <Input
                list="datalist-fournisseurs"
                placeholder="Nom du fournisseur (auto-créé si nouveau)"
                {...register('fournisseurNom', { required: false })}
              />
              <datalist id="datalist-fournisseurs">
                {fournisseurMatches.map((f) => (
                  <option key={f.id} value={f.nom}>
                    {f.nif ? `NIF ${f.nif} · ${f.nbFactures} fact.` : `${f.nbFactures} fact.`}
                  </option>
                ))}
              </datalist>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="NIF fournisseur">
                <Input placeholder="ex. 12345678" {...register('nifFournisseur')} />
              </Field>
              <Field label="N° facture">
                <Input placeholder="ex. F-2026-1234" {...register('numFacture')} />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Date facture">
                <Input type="date" {...register('dateFacture')} />
              </Field>
              {/* Champ beneficiaire cache — auto-rempli avec fournisseur a la submit */}
              <input type="hidden" {...register('beneficiaire')} />
            </div>
          </CardContent>
        </Card>

        {/* Montant + mode paiement */}
        <Card>
          <CardContent className="space-y-4 pt-6">
            {/* Marché noir : montant origine + taux éditables, total MRU dérivé read-only */}
            {watch('deviseSource') === 'marche_noir' ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label={`Montant ${watch('ocrDeviseDetectee') ?? 'devise'}`} required>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={
                        watch('ocrMontantOrigine') == null ? '' : String(watch('ocrMontantOrigine'))
                      }
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\s/g, '').replace(',', '.');
                        const parsed = raw === '' ? 0 : Number(raw);
                        const origine = isFinite(parsed) ? parsed : 0;
                        const taux = watch('ocrTauxChangeMru') ?? 0;
                        setValue('ocrMontantOrigine', origine, { shouldDirty: true });
                        setValue('montant', Math.round(origine * taux), {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                    />
                  </Field>
                  <Field
                    label={`Taux (MRU pour 1 ${watch('ocrDeviseDetectee') ?? 'devise'})`}
                    required
                  >
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={
                        watch('ocrTauxChangeMru') == null ? '' : String(watch('ocrTauxChangeMru'))
                      }
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\s/g, '').replace(',', '.');
                        const parsed = raw === '' ? 0 : Number(raw);
                        const taux = isFinite(parsed) ? parsed : 0;
                        const origine = watch('ocrMontantOrigine') ?? 0;
                        setValue('ocrTauxChangeMru', taux, { shouldDirty: true });
                        setValue('montant', Math.round(origine * taux), {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                    />
                  </Field>
                </div>
                <Field label="Total MRU à payer au contact local (calculé)">
                  <Input
                    type="text"
                    readOnly
                    disabled
                    value={watch('montant') ? formatMRU(watch('montant')) : ''}
                    className="bg-muted/40 cursor-not-allowed font-mono"
                  />
                  <p className="text-muted-foreground text-xs">
                    = {watch('ocrMontantOrigine')} {watch('ocrDeviseDetectee')} ×{' '}
                    {watch('ocrTauxChangeMru')} (modifie le taux ou le montant origine pour ajuster)
                  </p>
                </Field>
              </div>
            ) : (
              <Field
                label={`Montant ${watch('deviseSource') === 'banque' && watch('devise') ? watch('devise') : 'MRU'}`}
                required
                error={errors.montant?.message}
              >
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="ex. 125000"
                  value={
                    watch('montant') === 0 || watch('montant') == null
                      ? ''
                      : String(watch('montant'))
                  }
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\s/g, '').replace(',', '.');
                    const parsed = raw === '' ? 0 : Number(raw);
                    setValue('montant', isFinite(parsed) ? parsed : 0, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                />
                {watch('deviseSource') === 'banque' && (
                  <p className="text-muted-foreground text-xs">
                    SWIFT : montant fixé en {watch('devise')}. Taux MRU effectif saisi à
                    l&apos;upload du SWIFT confirmé.
                  </p>
                )}
              </Field>
            )}

            <Field label="Mode de paiement" required>
              <div className="flex flex-wrap gap-2">
                <RadioPill
                  checked={modePaiement === 'wallet'}
                  onClick={() => setValue('modePaiementSouhaite', 'wallet')}
                >
                  Wallet mobile
                </RadioPill>
                <RadioPill
                  checked={modePaiement === 'cheque'}
                  onClick={() => setValue('modePaiementSouhaite', 'cheque')}
                >
                  Chèque
                </RadioPill>
                <RadioPill
                  checked={modePaiement === 'transfer'}
                  onClick={() => setValue('modePaiementSouhaite', 'transfer')}
                >
                  Transfert SWIFT
                </RadioPill>
              </div>
            </Field>

            {modePaiement === 'wallet' && (
              <div className="space-y-3">
                {/* ECR n'a qu'un seul wallet : Bankily (décision Sidi 2026-08-20). */}
                <p className="text-muted-foreground text-xs">
                  Payé via <strong>Bankily</strong> (le compte Bankily sera débité).
                </p>
                <Field label="Numéro Bankily du bénéficiaire" required>
                  <Input placeholder="ex. 36123456" {...register('walletNumero')} />
                </Field>
              </div>
            )}

            {modePaiement === 'cheque' && (
              <Field label="Compte bancaire émetteur" required>
                <select
                  {...register('paidCompteId')}
                  className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                >
                  <option value="">— Choisir —</option>
                  {comptesBanque.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {modePaiement === 'transfer' && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Devise SWIFT" required>
                  <div className="flex gap-2">
                    <RadioPill checked={devise === 'EUR'} onClick={() => setValue('devise', 'EUR')}>
                      EUR
                    </RadioPill>
                    <RadioPill checked={devise === 'USD'} onClick={() => setValue('devise', 'USD')}>
                      USD
                    </RadioPill>
                  </div>
                </Field>
                <Field label="Compte bancaire émetteur" required>
                  <select
                    {...register('paidCompteId')}
                    className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                  >
                    <option value="">— Choisir —</option>
                    {comptesBanque.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}

            {/* Facture libellée en devise étrangère ? → modal source (banque vs marché noir) */}
            {!watch('ocrDeviseDetectee') && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Globe className="text-muted-foreground size-4" />
                <span className="text-muted-foreground text-xs">Facture en devise étrangère ?</span>
                {(['EUR', 'USD'] as const).map((d) => (
                  <Button
                    key={d}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setValue('ocrDeviseDetectee', d);
                      setShowDeviseModal(true);
                    }}
                  >
                    {d}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Catégorie + description + imputations */}
        <Card>
          <CardContent className="space-y-4 pt-6">
            <Field label="Catégorie business (libre)">
              <Input placeholder="ex. Pneus camion, Carburant moto…" {...register('categorie')} />
            </Field>

            <Field label="Description (optionnel)">
              <textarea
                {...register('description')}
                rows={2}
                className="border-input bg-background flex w-full rounded-md border px-3 py-2 text-sm"
                placeholder="Notes / précisions"
              />
            </Field>
          </CardContent>
        </Card>

        {/* Justificatif manuel (photo / PDF, archivé avec la dépense) */}
        <Card>
          <CardContent className="space-y-2 pt-6">
            <Label>Justificatif (photo ou PDF, optionnel)</Label>
            {justifFile ? (
              <div className="border-border flex items-center gap-3 rounded border p-2 text-sm">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Justificatif"
                    className="size-12 shrink-0 rounded border object-cover"
                  />
                ) : (
                  <FileText className="text-muted-foreground size-6 shrink-0" />
                )}
                <span className="flex-1 truncate">{justifFile.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setJustifFile(null);
                    setImagePreview(null);
                  }}
                  className="text-muted-foreground hover:text-red-600"
                  aria-label="Supprimer le justificatif"
                >
                  <X className="size-4" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2 sm:flex-row">
                <label className="border-primary bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md px-4 text-sm font-medium">
                  <Camera className="size-4" />
                  Prendre une photo
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleJustifSelected(f);
                      e.target.value = '';
                    }}
                  />
                </label>
                <label className="border-input hover:bg-accent inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium">
                  <Upload className="size-4" />
                  Choisir un fichier
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleJustifSelected(f);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            )}
            <p className="text-muted-foreground text-xs">
              JPG, PNG, WebP (5 MB) ou PDF (10 MB). Archivé avec la dépense.
            </p>
          </CardContent>
        </Card>

        {/* Double validation « 4 yeux » : prévenir avant d'enregistrer */}
        {isDoubleValidationRequired({
          montant: watch('montant'),
          modePaiementSouhaite: modePaiement,
        }) && (
          <Card className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
            <CardContent className="flex items-start gap-3 py-3">
              <AlertTriangle className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-sm">
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  Double validation requise
                </p>
                <p className="text-amber-800 dark:text-amber-200">
                  {modePaiement === 'transfer'
                    ? 'Tout transfert SWIFT'
                    : `Au-delà de ${formatMRU(SEUIL_DOUBLE_VALIDATION_MRU)}`}
                  , la dépense devra être validée par l&apos;autre DAF avant que la responsable
                  financière puisse la payer.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sticky submit bar — au-dessus de la bottom nav mobile (h-16) */}
        <div className="bg-background border-border fixed inset-x-0 bottom-16 z-40 flex items-center justify-between gap-2 border-t p-3 pb-[env(safe-area-inset-bottom,0)] sm:relative sm:bottom-0 sm:justify-end sm:border-0 sm:p-0 sm:pt-2">
          <Link to="/depenses" className="flex-1 sm:flex-initial">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button type="submit" disabled={isSubmitting} className="flex-1 gap-2 sm:flex-initial">
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            Enregistrer
          </Button>
        </div>
      </form>

      {/* Modal devise (marché noir vs SWIFT) — feedback Sidi 2026-05-16 */}
      {showDeviseModal && (
        <DeviseSourceModal
          devise={(watch('ocrDeviseDetectee') as 'EUR' | 'USD') || 'EUR'}
          montantOrigine={watch('ocrMontantOrigine') ?? 0}
          tauxSuggere={watch('ocrTauxChangeMru') ?? 0}
          comptes={comptesBanque}
          onClose={() => setShowDeviseModal(false)}
          onChoose={(choice) => {
            if (choice.source === 'marche_noir') {
              setValue('deviseSource', 'marche_noir');
              setValue('marcheNoirContact', choice.contact);
              setValue('ocrTauxChangeMru', choice.taux);
              setValue('modePaiementSouhaite', choice.modePaiement);
              setValue('devise', ''); // pas de SWIFT
              // Montant en MRU = montantOrigine × taux (payé au contact local)
              setValue('ocrMontantOrigine', choice.montantOrigine);
              setValue('montant', Math.round(choice.montantOrigine * choice.taux));
            } else {
              setValue('deviseSource', 'banque');
              setValue('modePaiementSouhaite', 'transfer');
              setValue('devise', choice.devise);
              setValue('paidCompteId', choice.paidCompteId);
              // Montant fixé en devise étrangère, taux saisi à l'upload SWIFT plus tard
              setValue('ocrMontantOrigine', choice.montantOrigine);
              setValue('montant', choice.montantOrigine);
              setValue('ocrTauxChangeMru', null);
            }
            setShowDeviseModal(false);
          }}
        />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// Modal devise (marché noir vs SWIFT bancaire)
// ═════════════════════════════════════════════════════════
interface DeviseSourceModalProps {
  devise: 'EUR' | 'USD';
  montantOrigine: number;
  tauxSuggere: number;
  /** Comptes bancaires ECR (émetteurs SWIFT possibles). */
  comptes: CompteTresorerie[];
  onClose: () => void;
  onChoose: (
    choice:
      | {
          source: 'marche_noir';
          contact: string;
          taux: number;
          montantOrigine: number;
          modePaiement: 'wallet' | 'cheque';
        }
      | {
          source: 'banque';
          devise: 'EUR' | 'USD';
          paidCompteId: string;
          montantOrigine: number;
        },
  ) => void;
}

function DeviseSourceModal({
  devise,
  montantOrigine: montantOrigineInitial,
  tauxSuggere,
  comptes,
  onClose,
  onChoose,
}: DeviseSourceModalProps) {
  const [mode, setMode] = useState<'choose' | 'marche_noir' | 'banque'>('choose');
  const [contact, setContact] = useState('');
  const [taux, setTaux] = useState(tauxSuggere > 0 ? String(tauxSuggere) : '');
  const [montantStr, setMontantStr] = useState(
    montantOrigineInitial > 0 ? String(montantOrigineInitial) : '',
  );
  const [modePaiementMN, setModePaiementMN] = useState<'wallet' | 'cheque'>('cheque');
  const [paidCompteId, setPaidCompteId] = useState<string>(comptes[0]?.id ?? '');

  // Si les comptes arrivent après l'ouverture du modal, présélectionne le premier.
  useEffect(() => {
    if (!paidCompteId && comptes[0]) setPaidCompteId(comptes[0].id);
  }, [paidCompteId, comptes]);

  const tauxNum = Number(taux.replace(',', '.')) || 0;
  const montantOrigine = Number(montantStr.replace(/\s/g, '').replace(',', '.')) || 0;
  const montantMRU = montantOrigine * tauxNum;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={onClose}
    >
      <div
        className="bg-card w-full max-w-lg rounded-t-xl sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4 p-5">
          {mode === 'choose' && (
            <>
              <div className="flex items-start gap-3">
                <Globe className="size-6 shrink-0 text-purple-600 dark:text-purple-400" />
                <div>
                  <h2 className="text-foreground text-lg font-semibold">
                    Facture en {devise} — comment vas-tu payer ?
                  </h2>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Mauritanie : accès devise limité. 2 options possibles.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setMode('banque')}
                  className="border-input hover:border-primary hover:bg-accent flex w-full items-start gap-3 rounded-md border p-3 text-left transition-all"
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                    🏦
                  </div>
                  <div>
                    <p className="text-foreground font-medium">SWIFT bancaire (BMCI / BCI)</p>
                    <p className="text-muted-foreground text-xs">
                      Paiement direct au fournisseur étranger via banque. Taux exact connu seulement
                      à l&apos;upload du SWIFT confirmé.
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode('marche_noir')}
                  className="border-input hover:border-primary hover:bg-accent flex w-full items-start gap-3 rounded-md border p-3 text-left transition-all"
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
                    🤝
                  </div>
                  <div>
                    <p className="text-foreground font-medium">Marché noir local</p>
                    <p className="text-muted-foreground text-xs">
                      Tu paies un contact en MRU (wallet ou chèque) ici à Nouakchott, qui règle la
                      devise à l&apos;étranger. Taux fixé maintenant.
                    </p>
                  </div>
                </button>
              </div>
              <div className="flex justify-end">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Plus tard
                </Button>
              </div>
            </>
          )}

          {mode === 'banque' && (
            <>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🏦</span>
                <div>
                  <h2 className="text-foreground text-lg font-semibold">SWIFT bancaire</h2>
                  <p className="text-muted-foreground text-sm">
                    Montant fixé en {devise}. Le taux sera saisi à l&apos;upload du SWIFT confirmé.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="after:text-red-500 after:content-['*']">
                    Montant à transférer ({devise})
                  </Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={montantStr}
                    onChange={(e) => setMontantStr(e.target.value)}
                    placeholder="ex. 12500"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Compte bancaire émetteur</Label>
                  <select
                    value={paidCompteId}
                    onChange={(e) => setPaidCompteId(e.target.value)}
                    className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
                  >
                    {comptes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-between">
                <Button type="button" variant="ghost" onClick={() => setMode('choose')}>
                  ← Retour
                </Button>
                <Button
                  type="button"
                  disabled={montantOrigine <= 0 || !paidCompteId}
                  onClick={() => {
                    onChoose({
                      source: 'banque',
                      devise,
                      paidCompteId,
                      montantOrigine,
                    });
                  }}
                >
                  Confirmer SWIFT
                </Button>
              </div>
            </>
          )}

          {mode === 'marche_noir' && (
            <>
              <div className="flex items-start gap-3">
                <span className="text-2xl">🤝</span>
                <div>
                  <h2 className="text-foreground text-lg font-semibold">Marché noir local</h2>
                  <p className="text-muted-foreground text-sm">
                    Contact local payé en MRU. Le taux est fixé maintenant.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="after:text-red-500 after:content-['*']">
                    Montant facture ({devise})
                  </Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={montantStr}
                    onChange={(e) => setMontantStr(e.target.value)}
                    placeholder="ex. 12500"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="after:text-red-500 after:content-['*']">
                    Nom du contact local
                  </Label>
                  <Input
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="ex. Mohamed Salem (bureau change Nouakchott)"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="after:text-red-500 after:content-['*']">
                    Taux convenu (MRU pour 1 {devise})
                  </Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={taux}
                    onChange={(e) => setTaux(e.target.value)}
                    placeholder="ex. 45.5"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Mode de paiement local</Label>
                  <div className="flex gap-2">
                    <RadioPill
                      checked={modePaiementMN === 'wallet'}
                      onClick={() => setModePaiementMN('wallet')}
                    >
                      Wallet
                    </RadioPill>
                    <RadioPill
                      checked={modePaiementMN === 'cheque'}
                      onClick={() => setModePaiementMN('cheque')}
                    >
                      Chèque
                    </RadioPill>
                  </div>
                </div>
                {tauxNum > 0 && montantOrigine > 0 && (
                  <div className="text-foreground bg-muted/50 rounded p-3 text-sm">
                    Tu paieras{' '}
                    <span className="font-mono font-semibold">
                      {Math.round(montantMRU).toLocaleString('fr-FR')} MRU
                    </span>{' '}
                    au contact local pour {montantOrigine} {devise}
                  </div>
                )}
              </div>
              <div className="flex justify-between">
                <Button type="button" variant="ghost" onClick={() => setMode('choose')}>
                  ← Retour
                </Button>
                <Button
                  type="button"
                  disabled={!contact.trim() || tauxNum <= 0 || montantOrigine <= 0}
                  onClick={() => {
                    onChoose({
                      source: 'marche_noir',
                      contact: contact.trim(),
                      taux: tauxNum,
                      montantOrigine,
                      modePaiement: modePaiementMN,
                    });
                  }}
                >
                  Confirmer marché noir
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════
// Utils
// ═════════════════════════════════════════════════════════

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, required, error, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <Label className={cn(required && "after:text-red-500 after:content-['*']")}>{label}</Label>
      {children}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Phases OCR : upload de la photo → analyse → form pré-rempli
// ════════════════════════════════════════════════════════════

function UploadPhase({
  onFileSelected,
  onManual,
}: {
  onFileSelected: (file: File) => void;
  onManual: () => void;
}) {
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/depenses">
          <Button variant="ghost" size="icon" aria-label="Retour">
            <ArrowLeft className="size-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
            Nouvelle dépense
          </h1>
          <p className="text-muted-foreground text-sm">
            Photographie la facture — l&apos;IA pré-remplit le formulaire
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 py-8">
          <div className="flex flex-col items-center gap-2 text-center">
            <span className="bg-primary/10 text-primary flex size-14 items-center justify-center rounded-full">
              <ScanLine className="size-7" />
            </span>
            <p className="text-foreground font-medium">Scanner la facture</p>
            <p className="text-muted-foreground max-w-sm text-sm">
              Photo ou PDF de la facture fournisseur. Fournisseur, montant, n° de facture, lignes et
              devise sont extraits automatiquement — tu vérifies avant d&apos;enregistrer.
            </p>
          </div>
          <div className="mx-auto flex max-w-md flex-col gap-2 sm:flex-row">
            <label className="border-primary bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md px-4 text-sm font-medium">
              <Camera className="size-4" />
              Prendre une photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFileSelected(f);
                  e.target.value = '';
                }}
              />
            </label>
            <label className="border-input hover:bg-accent inline-flex h-12 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium">
              <Upload className="size-4" />
              Choisir un fichier
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFileSelected(f);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <p className="text-muted-foreground text-center text-xs">
            JPG, PNG, WebP (5 MB) ou PDF (10 MB). HEIC iPhone : convertis en JPG.
          </p>
          <div className="flex justify-center">
            <Button type="button" variant="ghost" className="gap-2" onClick={onManual}>
              <PenLine className="size-4" />
              Saisie manuelle (sans photo)
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ProcessingPhase() {
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <Loader2 className="text-primary size-10 animate-spin" />
          <p className="text-foreground font-medium">Analyse de la facture en cours…</p>
          <p className="text-muted-foreground max-w-sm text-sm">
            L&apos;IA lit le fournisseur, le montant, les lignes et la devise. Quelques secondes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function OcrResultBanner({
  result,
  imagePreview,
  onRescan,
}: {
  result: OcrFactureResult;
  imagePreview: string | null;
  onRescan: () => void;
}) {
  const nbLignes = result.factures.reduce((acc, f) => acc + f.lignes.length, 0);
  const confianceColors: Record<OcrFactureResult['ocr_confiance'], string> = {
    haute: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    moyenne: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300',
    basse: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
  };
  return (
    <Card className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30">
      <CardContent className="flex items-center gap-3 py-3">
        {imagePreview ? (
          <img
            src={imagePreview}
            alt="Facture"
            className="size-12 shrink-0 rounded border object-cover"
          />
        ) : (
          <FileText className="size-8 shrink-0 text-blue-600 dark:text-blue-400" />
        )}
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-blue-900 dark:text-blue-100">
            <Sparkles className="size-3.5" />
            Extrait par {result.ocr_model}
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
                confianceColors[result.ocr_confiance],
              )}
            >
              {result.ocr_confiance}
            </span>
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-300">
            {result.factures.length} facture{result.factures.length > 1 ? 's' : ''} · {nbLignes}{' '}
            ligne{nbLignes > 1 ? 's' : ''} — vérifie chaque champ avant d&apos;enregistrer.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0 gap-1.5"
          onClick={onRescan}
        >
          <RefreshCw className="size-3.5" />
          Re-scanner
        </Button>
      </CardContent>
    </Card>
  );
}

interface RadioPillProps {
  checked: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function RadioPill({ checked, onClick, children }: RadioPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
        checked
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-input text-muted-foreground hover:bg-accent',
      )}
    >
      {children}
    </button>
  );
}
